import * as echarts from 'echarts/core';
import { CSSProperties, useEffect, useRef } from 'react';
import { ECOption } from '../../model';
import { GetformatMoney } from '../../pages/component/billing-center/billing-overview/component/fomat-money';
import { renderToString } from 'react-dom/server';

interface IProps {
  className?: string;
  style?: CSSProperties;
  xAxisLabel: ReadonlyArray<string>;
  seriesData: ReadonlyArray<Array<number | null>>;
  defaultMonth?: string;
}

export const ChartSmoothLine = (props: IProps) => {
  const chartContainer = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<any>(null);
  const { style, seriesData, xAxisLabel, defaultMonth } = props;

  const formatYAxisLabel = (value: number | null) => {
    if (value === null) {
      return '-'; // 或者返回 'N/A' 等
    }
    if (value >= 10000) {
      return `${(value / 10000).toFixed(0)}w`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}k`;
    }
    return value.toString();
  };

  useEffect(() => {
    if (!chartContainer.current) {
      return;
    }
    chartInstance.current = echarts.init(chartContainer.current);

    const option: ECOption = {
      visualMap: {
        show: false,
        type: 'continuous',
        seriesIndex: 1,
        dimension: 0,
        min: 0,
        max: seriesData.length - 1,
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'line',
          label: {
            backgroundColor: '#6a7985',
          },
        },
        formatter: (params: any) => {
          const { value } = params[0]; // 假设Y轴的值在第一个系列中
          const formattedValue = renderToString(
            <GetformatMoney
              className="normal-balance-detail-money month-money"
              money={value}
            />
          );
          return `${formattedValue}`;
        },
        extraCssText:
          'height: 34px; display: flex; align-items: center;border-radius: 8px; box-shadow: 0 0 10px 0 #1d21291a;font-size:12px; font-weight: 400;',
        position: (point, params, dom, rect, size) => {
          // point 是鼠标的坐标 [x, y]
          const tooltipHeight = size.contentSize[1]; // tooltip 的高度
          return [point[0] - 28, point[1] - tooltipHeight - 20]; // 在 Y 轴上方 tooltipHeight + 15px
        },
      },
      xAxis: {
        data: [...xAxisLabel],
        axisLine: {
          show: true, // 显示X轴线
          lineStyle: {
            color: '#C9CDD4', // 轴线颜色
          },
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          color: '#4E5969', // X轴文本颜色
          fontSize: 12,
        },
        interval: 0, // 强制显示所有标签
      },
      yAxis: {
        type: 'value',
        min: 0, // 最小值
        scale: true,
        splitLine: {
          show: false, // 设置为false，不展示背景的横线
        },
        axisLabel: {
          color: '#86909C', // 设置Y轴文本颜色为#86909C
          interval: 0,
          formatter: formatYAxisLabel,
        },
        splitNumber: 4,
      },
      grid: {
        show: false, // 设置为false，不展示图表背景
      },
      series: seriesData.map((series) => {
        // 有效值是否只有一个
        const validData = series.filter((value) => value !== null);
        const validValuesCount = validData.length;
        return {
          data: series,
          type: 'line',
          smooth: true,
          showSymbol: validValuesCount === 1, // 是否显示数据点
          connectNulls: true, // 允许连接 null 值
          symbol: 'circle', // 设置小圆点的形状为圆形
          symbolSize: 14, // 设置小圆点的默认半径
          itemStyle: {
            color: '#fff', // 默认数据点颜色
            borderColor: 'rgba(22, 93, 255, 1)', // 默认边框颜色
            borderWidth: 3, // 默认边框宽度
          },
          lineStyle: {
            color: {
              type: 'linear',
              x: 1,
              y: 0,
              x2: 0,
              y2: 0,
              colorStops: [
                { offset: 0, color: 'rgba(22, 93, 255, 1)' },
                { offset: 1, color: 'rgba(232, 243, 255, 1)' },
              ],
            },
          },
          emphasis: {
            itemStyle: {
              color: 'white', // 鼠标悬停时数据点的颜色
              borderColor: 'rgba(22, 93, 255, 1)', // 鼠标悬停时边框颜色
              borderWidth: 3, // 鼠标悬停时边框宽度
              symbolSize: 14, // 鼠标悬停时小圆点的半径
            },
          },
          markPoint: {
            data: !!defaultMonth
              ? [
                  {
                    // type: 'max',
                    name: '',
                    coord: [
                      defaultMonth,
                      series[xAxisLabel.indexOf(defaultMonth)] ?? 0,
                    ], // `coord` 是 [x, y] 格式
                    symbol: 'circle',
                    symbolSize: 14,
                  },
                ]
              : [],
          },
        };
      }),
    };
    chartInstance.current.setOption(option);
    const handleResize = () => {
      chartInstance.current?.resize();
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [seriesData]);

  return (
    <div style={style} ref={chartContainer} className={props.className}></div>
  );
};
